import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { meApi } from "../../api";
import Sidebar from "../layout/Sidebar";
import BookAppointment from "../appointment/book/BookAppointment";
import classes from "./Dashboard.module.scss";

// ── Chart constants ───────────────────────────────────────
const CW = 560,
  CH = 140;
const PL = 52,
  PR = 16,
  PT = 16,
  PB = 28;
const plotW = CW - PL - PR;
const plotH = CH - PT - PB;

// ── Types ─────────────────────────────────────────────────
type Measurement = {
  id: string;
  date: string;
  weight?: number;
  bodyFatPct?: number;
  musclePct?: number;
  waterPct?: number;
  waist?: number;
  arm?: number;
  wrist?: number;
  hips?: number;
  fatClipper?: number;
  visceralFat?: number;
};
type MealItem = {
  id: number;
  name: string;
  quantity: string;
  cal: number;
  protein: number;
  fat: number;
  carb: number;
};
type MealSection = { id: string; type: string; items: MealItem[] };
type WeekPlan = { sections?: MealSection[]; clientSelections?: Record<string, number[]> };
type DietPlan = Record<string, WeekPlan>;
type Profile = {
  name: string;
  status?: string;
  currentWeight?: number;
  startWeight?: number;
  nextApt?: string | null;
  nextAptType?: string;
  nextAptSlotId?: string | null;
  unlockedWeeks?: string[];
  bodyFatPct?: number;
  musclePct?: number;
  waterPct?: number;
};

const WEEKS = ["Week 1", "Week 2", "Week 3", "Week 4"];
const MEAL_EMOJI: Record<string, string> = {
  Breakfast: "🥣",
  Lunch: "🍗",
  Dinner: "🥗",
  Snack: "🍎",
  Supplement: "💊",
};

const CalIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

// ── Component ─────────────────────────────────────────────
const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<Profile | null>(null);
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [dietPlan, setDietPlan] = useState<DietPlan>({});
  const [loading, setLoading] = useState(true);

  const [selectedWeek, setSelectedWeek] = useState("Week 1");
  const [activeChart, setActiveChart] = useState<
    "Weight" | "Body Fat" | "Muscle"
  >("Weight");
  const [showReschedule, setShowReschedule] = useState(false);

  useEffect(() => {
    Promise.all([
      meApi.profile().catch(() => null),
      meApi.getMeasurements().catch(() => ({ measurements: [] })),
      meApi.getDietPlan().catch(() => ({ dietPlan: {} })),
    ])
      .then(([pd, md, dd]) => {
        if (pd?.user) setProfile(pd.user);
        if (md?.measurements) setMeasurements(md.measurements);
        if (dd?.dietPlan) setDietPlan(dd.dietPlan);
        // Default to first unlocked week
        const unlocked = pd?.user?.unlockedWeeks ?? ["Week 1"];
        if (unlocked.length) setSelectedWeek(unlocked[0]);
      })
      .finally(() => setLoading(false));
  }, []);

  const userName = profile?.name ?? user?.name ?? "—";
  const currentWeight = profile?.currentWeight ?? null;
  const unlockedWeeks = profile?.unlockedWeeks ?? ["Week 1"];
  // ── Latest measurement ────────────────────────────────
  const latest = measurements[0] ?? null;
  const bodyFatPct = profile?.bodyFatPct ?? null;
  const musclePct = profile?.musclePct ?? null;
  const waterPct = profile?.waterPct ?? null;

  // ── Chart data ────────────────────────────────────────
  const getChartValue = (m: Measurement): number | undefined =>
    activeChart === "Weight"
      ? m.weight
      : activeChart === "Body Fat"
        ? m.bodyFatPct
        : m.musclePct;
  const unitLabel = activeChart === "Weight" ? "kg" : "%";

  const chartData = [...measurements]
    .reverse()
    .map((m) => ({ date: m.date, value: getChartValue(m) }))
    .filter((d): d is { date: string; value: number } => d.value != null);

  const values = chartData.map((d) => d.value);
  const dataMin = values.length ? Math.min(...values) : 0;
  const dataMax = values.length ? Math.max(...values) : 100;
  const range = dataMax - dataMin || 1;
  const yPad = range * 0.3;
  const yMin = dataMin - yPad;
  const yMax = dataMax + yPad;
  const mid = (yMin + yMax) / 2;

  const toX = (i: number) =>
    PL + (i / Math.max(chartData.length - 1, 1)) * plotW;
  const toY = (v: number) => PT + (1 - (v - yMin) / (yMax - yMin)) * plotH;

  const chartPts = chartData.map((d, i) => ({
    ...d,
    x: toX(i),
    y: toY(d.value),
  }));
  const polylineStr = chartPts.map((p) => `${p.x},${p.y}`).join(" ");
  const areaPathStr = chartPts.length
    ? `M${chartPts[0].x},${PT + plotH} ${chartPts.map((p) => `L${p.x},${p.y}`).join(" ")} L${chartPts[chartPts.length - 1].x},${PT + plotH} Z`
    : "";
  const tipPt = chartPts[chartPts.length - 1];
  const tipDelta =
    values.length >= 2 ? values[values.length - 1] - values[0] : 0;
  const tipLabel = `${tipDelta > 0 ? "+" : ""}${tipDelta.toFixed(1)} ${unitLabel}`;

  // ── Diet plan for selected week ───────────────────────
  const weekPlan = dietPlan[selectedWeek] ?? {};
  const weekSections: MealSection[] = weekPlan.sections ?? [];
  const clientSel = weekPlan.clientSelections ?? {};
  const selectedItems = (sec: MealSection) => {
    const sel = clientSel[sec.id];
    return sel ? sec.items.filter(i => sel.includes(i.id)) : sec.items;
  };
  const dayTotal   = weekSections.reduce((s, sec) => s + selectedItems(sec).reduce((a, i) => a + (i.cal     ?? 0), 0), 0);
  const dayProtein = weekSections.reduce((s, sec) => s + selectedItems(sec).reduce((a, i) => a + (i.protein ?? 0), 0), 0);
  const dayFat     = weekSections.reduce((s, sec) => s + selectedItems(sec).reduce((a, i) => a + (i.fat     ?? 0), 0), 0);

  if (loading)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            border: "3px solid #EBF7EB",
            borderTopColor: "#4A7C40",
            borderRadius: "50%",
            animation: "spin 0.7s linear infinite",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );

  return (
    <div className={classes.layout}>
      <Sidebar />

      {/* ── Main ── */}
      <div className={classes.main_wrap}>
        <header className={classes.topbar}>
          <div className={classes.topbar_right}>
            <div className={classes.avatar}>
              {userName[0]?.toUpperCase() ?? "?"}
            </div>
          </div>
        </header>

        <main className={classes.content}>
          <div className={classes.greeting}>
            <h1 className={classes.greeting_title}>Hello, {userName} 👋</h1>
            <p className={classes.greeting_sub}>Here's your health summary</p>
          </div>

          {/* Metrics */}
          <div className={classes.metrics_grid}>
            <div className={classes.metric_card}>
              <div className={classes.metric_header}>
                <span className={classes.metric_icon}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 512 512"
                    fill="currentColor"
                  >
                    <path
                      d="M466.286,0H45.714C20.507,0,0,20.507,0,45.714v420.571C0,491.493,20.507,512,45.714,512h420.571
                      C491.493,512,512,491.493,512,466.286V45.714C512,20.507,491.493,0,466.286,0z M493.714,466.286
                      c0,15.122-12.306,27.429-27.429,27.429H45.714c-15.122,0-27.429-12.306-27.429-27.429V45.714
                      c0-15.122,12.306-27.429,27.429-27.429h420.571c15.122,0,27.429,12.306,27.429,27.429V466.286z"
                    />
                    <path
                      d="M310.857,45.714H201.143c-15.122,0-27.429,12.306-27.429,27.429v64c0,15.122,12.306,27.429,27.429,27.429h109.714
                      c15.122,0,27.429-12.306,27.429-27.429v-64C338.286,58.021,325.979,45.714,310.857,45.714z M320,137.143
                      c0,5.038-4.096,9.143-9.143,9.143H201.143c-5.047,0-9.143-4.105-9.143-9.143v-64c0-5.038,4.096-9.143,9.143-9.143h109.714
                      c5.047,0,9.143,4.105,9.143,9.143V137.143z"
                    />
                    <path
                      d="M137.143,182.857c-45.376,0-82.286,28.709-82.286,64v172.142c0,24.192,18.121,44.864,41.262,47.077
                      c1.454,0.137,2.907,0.21,4.352,0.21c11.365,0,22.418-4.215,30.848-11.877c9.509-8.658,14.967-20.992,14.967-34.661
                      c0-47.863,20.343-95.808,57.271-134.976c10.377-11.026,15.872-24.128,15.872-37.915
                      C219.429,211.566,182.519,182.857,137.143,182.857z M190.254,272.219C150.107,314.807,128,367.195,128,420.571
                      c0,7.707-3.273,15.113-8.978,20.297c-5.797,5.275-13.339,7.753-21.166,7.003c-13.851-1.326-24.713-14.007-24.713-28.873V246.857
                      c0-25.207,28.718-45.714,64-45.714s64,20.507,64,45.714C201.143,255.89,197.376,264.667,190.254,272.219z"
                    />
                    <path
                      d="M374.857,182.857c-45.376,0-82.286,28.709-82.286,64c0,13.787,5.495,26.889,15.872,37.906
                      c36.928,39.177,57.271,87.113,57.271,135.808c0,12.846,5.458,25.179,14.967,33.829c8.43,7.671,19.483,11.886,30.848,11.886
                      c1.445,0,2.907-0.064,4.361-0.201c23.131-2.222,41.253-22.894,41.253-47.086V246.857
                      C457.143,211.566,420.233,182.857,374.857,182.857z M438.857,418.999c0,14.866-10.862,27.547-24.704,28.873
                      c-7.845,0.75-15.387-1.728-21.166-7.003c-5.714-5.184-8.987-12.59-8.987-21.12c0-52.553-22.107-104.942-62.254-147.52
                      c-7.122-7.561-10.889-16.338-10.889-25.371c0-25.207,28.718-45.714,64-45.714s64,20.507,64,45.714V418.999z"
                    />
                    <rect
                      x="118.857"
                      y="82.286"
                      width="36.571"
                      height="18.286"
                    />
                    <rect
                      x="118.857"
                      y="118.857"
                      width="36.571"
                      height="18.286"
                    />
                    <rect x="246.857" y="448" width="18.286" height="18.286" />
                    <rect x="283.429" y="448" width="18.286" height="18.286" />
                    <rect x="210.286" y="448" width="18.286" height="18.286" />
                  </svg>
                </span>
                <span className={classes.metric_label}>Current Weight</span>
              </div>
              <div className={classes.metric_value}>
                <span className={classes.metric_num}>
                  {currentWeight ?? "—"}
                </span>
                <span className={classes.metric_unit}>
                  {currentWeight ? "KG" : ""}
                </span>
              </div>
            </div>

            <div className={classes.metric_card}>
              <div className={classes.metric_header}>
                <span className={classes.metric_icon}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 32 32"
                    fill="currentColor"
                  >
                    <path d="M 16.03125 3.46875 L 15.375 4.03125 C 15.375 4.03125 13.035156 5.941406 10.71875 8.71875 C 8.402344 11.496094 6 15.160156 6 19 C 6 21.765625 7.132813 24.070313 8.96875 25.625 C 10.691406 27.082031 12.996094 27.871094 15.5 27.96875 C 15.667969 27.976563 15.832031 28 16 28 C 16.167969 28 16.332031 27.976563 16.5 27.96875 C 19.003906 27.871094 21.308594 27.082031 23.03125 25.625 C 24.867188 24.070313 26 21.765625 26 19 C 26 15.542969 23.585938 11.941406 21.28125 9.0625 C 18.976563 6.183594 16.6875 4.0625 16.6875 4.0625 Z M 15.96875 6.25 C 16.488281 6.742188 17.851563 7.976563 19.71875 10.3125 C 21.914063 13.058594 24 16.558594 24 19 C 24 21.234375 23.132813 22.929688 21.71875 24.125 C 21.375 24.414063 21 24.679688 20.59375 24.90625 C 20.839844 24.316406 21 23.675781 21 23 C 21 16.75 17.65625 12.9375 17.65625 12.9375 L 15.84375 10.90625 L 15.90625 13.625 C 15.90625 13.625 15.910156 14.898438 15.75 16.125 C 15.667969 16.738281 15.554688 17.347656 15.40625 17.6875 C 15.363281 17.785156 15.34375 17.796875 15.3125 17.84375 C 15.207031 17.804688 14.890625 17.648438 14.59375 17.34375 C 14.257813 17 14.03125 16.65625 14.03125 16.65625 L 13.0625 15.15625 L 12.3125 16.78125 C 12.3125 16.78125 11 19.457031 11 23 C 11 23.675781 11.160156 24.316406 11.40625 24.90625 C 11 24.679688 10.625 24.414063 10.28125 24.125 C 8.867188 22.929688 8 21.234375 8 19 C 8 16.042969 10.097656 12.621094 12.28125 10 C 14.132813 7.78125 15.445313 6.695313 15.96875 6.25 Z M 17.65625 16.78125 C 18.339844 18.265625 19 20.339844 19 23 C 19 24.65625 17.65625 26 16 26 C 14.34375 26 13 24.65625 13 23 C 13 21.40625 13.308594 20.058594 13.59375 19.09375 C 14.042969 19.496094 14.59375 19.90625 15.40625 19.90625 C 15.882813 19.90625 16.335938 19.6875 16.625 19.40625 C 16.914063 19.125 17.078125 18.792969 17.21875 18.46875 C 17.445313 17.941406 17.5625 17.355469 17.65625 16.78125 Z" />
                  </svg>
                </span>
                <span className={classes.metric_label}>Body Fat</span>
              </div>
              <div className={classes.metric_value}>
                <span className={classes.metric_num}>{bodyFatPct ?? "—"}</span>
                <span className={classes.metric_unit}>
                  {bodyFatPct != null ? "%" : ""}
                </span>
              </div>
            </div>

            <div className={classes.metric_card}>
              <div className={classes.metric_header}>
                <span className={classes.metric_icon}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 512 512"
                    fill="currentColor"
                  >
                    <path
                      d="M477.604,261.355c-6.862-48.022-36.213-192.083-65.118-206.54c-2.605-1.302-6.609-3.484-11.663-6.23
                      C340.186,15.624,280.307-12.401,253.229,5.676c-20.993,14-39.012,46.582-41.908,75.794c-1.681,16.974,1.963,30.474,10.535,39.046
                      c18.787,18.797,81.533,11.002,114.607,5.292c-2.216,10.461-6.988,28.218-17.29,53.959c-16.532,41.33-7.62,106.03-2.838,133.399
                      h-21.479c-16.698-34.833-39.722-52.755-68.5-58.48c-56.798-11.342-116.59,36.087-130.614,48.527H59.704
                      c-5.501,0-9.952,4.456-9.952,9.952c0,5.496,4.451,9.952,9.952,9.952h39.809c2.498,0,4.908-1.263,6.745-2.955
                      c0.612-0.569,62.63-57.128,116.259-46.427c23.802,4.751,42.997,22.851,57.051,53.31c1.633,3.528,5.161,6.023,9.039,6.023h39.809
                      c3.023,0,5.87-1.691,7.766-4.048c1.885-2.352,2.604-5.597,1.953-8.543c-0.194-0.865-19.03-87.187-0.476-133.557
                      c20.031-50.097,20.614-71.168,20.614-73.403c0-2.979-1.341-5.828-3.645-7.718c-2.294-1.885-5.326-2.653-8.261-2.07
                      c-45.349,9.053-100.475,12.664-110.438,2.716c-4.092-4.092-5.841-12.482-4.801-23.017c2.323-23.471,16.882-50.355,33.142-61.192
                      c13.85-9.233,56.604,5.539,127.047,43.836c5.316,2.887,9.525,5.175,12.168,6.492c13.617,8.606,43.104,112.464,54.407,191.606
                      c8.426,58.96,2.206,148.546,0.399,171.39c-34.094,16.911-245.465,113.82-413.176,7.878c-4.655-2.921-10.788-1.541-13.733,3.1
                      c-2.935,4.65-1.545,10.798,3.1,13.733c61.055,38.555,127.349,51.73,189.473,51.725c133.044-0.005,246.874-60.443,248.536-61.342
                      c2.925-1.589,4.869-4.534,5.17-7.853C478.07,438.291,487.604,331.371,477.604,261.355z"
                    />
                  </svg>
                </span>
                <span className={classes.metric_label}>Muscle</span>
              </div>
              <div className={classes.metric_value}>
                <span className={classes.metric_num}>{musclePct ?? "—"}</span>
                <span className={classes.metric_unit}>
                  {musclePct != null ? "%" : ""}
                </span>
              </div>
            </div>

            <div className={classes.metric_card}>
              <div className={classes.metric_header}>
                <span className={classes.metric_icon}>
                  <svg width="20" height="20" viewBox="0 0 512 512" fill="none">
                    <path
                      d="M341.33 285.8C341.33 313.33 331.2 339.93 312.53 359.87C301.87 371.2 288.53 380.27 273.07 385.87M405.33 292.27C405.33 156.27 256 42.67 256 42.67C256 42.67 106.67 156.27 106.67 292.27C106.67 331.73 121.07 369.87 146.13 398.13C171.2 426.4 205.87 448 256 448C306.13 448 340.8 426.4 365.87 398.13C390.93 369.87 405.33 331.73 405.33 292.27Z"
                      stroke="#4A7C40"
                      stroke-width="40"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
                <span className={classes.metric_label}>Water %</span>
              </div>
              <div className={classes.metric_value}>
                <span className={classes.metric_num}>{waterPct ?? "—"}</span>
                <span className={classes.metric_unit}>
                  {waterPct != null ? "%" : ""}
                </span>
              </div>
            </div>
          </div>

          {/* Body Measurements */}
          {latest && (
            <div className={classes.measurements_card}>
              <div className={classes.measurements_header}>
                <h4 className={classes.card_title}>Body Measurements</h4>
              </div>
              <div className={classes.measurements_grid}>
                {[
                  { label: "Waist", value: latest.waist, unit: "cm" },
                  { label: "Arm", value: latest.arm, unit: "cm" },
                  { label: "Wrist", value: latest.wrist, unit: "cm" },
                  { label: "Hips", value: latest.hips, unit: "cm" },
                  {
                    label: "Fat Clipper",
                    value: latest.fatClipper,
                    unit: "mm",
                  },
                  ...(latest.visceralFat != null
                    ? [
                        {
                          label: "Visceral Fat",
                          value: latest.visceralFat,
                          unit: "%",
                        },
                      ]
                    : []),
                ].map((m) => (
                  <div key={m.label} className={classes.measurement_item}>
                    <span className={classes.measurement_label}>{m.label}</span>
                    <div className={classes.measurement_val_row}>
                      <span className={classes.measurement_num}>
                        {m.value ?? "—"}
                      </span>
                      <span className={classes.measurement_unit}>{m.unit}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Chart */}
          {measurements.length > 0 && (
            <div className={classes.chart_card}>
              <div className={classes.chart_header}>
                <h3 className={classes.chart_title}>Progress </h3>
                <div className={classes.chart_tabs}>
                  {(["Weight", "Body Fat", "Muscle"] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={`${classes.chart_tab} ${activeChart === t ? classes.chart_tab_active : ""}`}
                      onClick={() => setActiveChart(t)}
                    >
                      {t === "Muscle" ? "Muscle" : t === "Body Fat" ? "Fat" : t}
                    </button>
                  ))}
                </div>
              </div>

              <svg
                viewBox={`0 0 ${CW} ${CH}`}
                className={classes.chart_svg}
                preserveAspectRatio="none"
              >
                {[yMax, mid, yMin].map((v, gi) => (
                  <g key={gi}>
                    <line
                      x1={PL}
                      y1={toY(v)}
                      x2={CW - PR}
                      y2={toY(v)}
                      stroke="#E6EDE6"
                      strokeWidth="1"
                    />
                    <text
                      x={PL - 6}
                      y={toY(v) + 4}
                      textAnchor="end"
                      fill="#7A8C7A"
                      fontSize="10"
                    >
                      {Number.isInteger(v) ? v : v.toFixed(1)} {unitLabel}
                    </text>
                  </g>
                ))}
                {areaPathStr && (
                  <path d={areaPathStr} fill="#4A7C40" fillOpacity="0.08" />
                )}
                {chartPts.length > 1 && (
                  <polyline
                    points={polylineStr}
                    fill="none"
                    stroke="#4A7C40"
                    strokeWidth="2.5"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                )}
                {chartPts.map((p, i) => (
                  <circle
                    key={i}
                    cx={p.x}
                    cy={p.y}
                    r={i === chartPts.length - 1 ? 5 : 4}
                    fill="#4A7C40"
                    stroke="white"
                    strokeWidth="2"
                  />
                ))}
                {tipPt && (
                  <>
                    <rect
                      x={tipPt.x - 30}
                      y={tipPt.y - 34}
                      width="60"
                      height="22"
                      rx="6"
                      fill="#4A7C40"
                    />
                    <polygon
                      points={`${tipPt.x - 5},${tipPt.y - 12} ${tipPt.x + 5},${tipPt.y - 12} ${tipPt.x},${tipPt.y - 6}`}
                      fill="#4A7C40"
                    />
                    <text
                      x={tipPt.x}
                      y={tipPt.y - 18}
                      textAnchor="middle"
                      fill="white"
                      fontSize="11"
                      fontWeight="600"
                    >
                      {tipLabel}
                    </text>
                  </>
                )}
                {chartPts.map((p, i) => (
                  <text
                    key={i}
                    x={p.x}
                    y={CH - 4}
                    textAnchor="middle"
                    fill="#7A8C7A"
                    fontSize="9"
                  >
                    {p.date}
                  </text>
                ))}
              </svg>
              <div className={classes.chart_legend}>
                <span className={classes.legend_dot} />
                <span className={classes.legend_label}>
                  {activeChart === "Body Fat"
                    ? "Body Fat (%)"
                    : activeChart === "Muscle"
                      ? "Muscle (%)"
                      : "Weight (kg)"}
                </span>
              </div>
            </div>
          )}

          {/* Diet Plan */}
          <div className={classes.bottom_grid}>
            <div className={classes.card}>
              <div className={classes.card_header}>
                <h4 className={classes.card_title}>Diet Plan</h4>
                <button
                  type="button"
                  className={classes.dots_btn}
                  onClick={() => navigate("/diet-plans")}
                >
                  View All
                </button>
              </div>

              <div className={classes.day_tabs}>
                {WEEKS.map((w) => {
                  const unlocked = unlockedWeeks.includes(w);
                  return (
                    <button
                      key={w}
                      type="button"
                      disabled={!unlocked}
                      className={`${classes.day_tab} ${selectedWeek === w ? classes.day_tab_active : ""} ${!unlocked ? classes.day_tab_locked : ""}`}
                      onClick={() => unlocked && setSelectedWeek(w)}
                      title={
                        !unlocked
                          ? "Not yet available — your dietitian will unlock this week"
                          : undefined
                      }
                    >
                      {w}
                      {!unlocked && (
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ marginLeft: 4, flexShrink: 0 }}
                        >
                          <rect x="3" y="11" width="18" height="11" rx="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>

              {weekSections.length === 0 ? (
                <p
                  style={{
                    color: "#999",
                    fontSize: 13,
                    padding: "16px 0",
                    textAlign: "center",
                  }}
                >
                  No plan assigned for this week yet.
                </p>
              ) : (
                <>
                  <div className={classes.meal_list}>
                    {weekSections.map((sec) => {
                      const items = selectedItems(sec);
                      if (!items.length) return null;
                      return (
                        <div key={sec.id} className={classes.meal_item}>
                          <span className={classes.meal_emoji}>
                            {MEAL_EMOJI[sec.type] ?? "🍽️"}
                          </span>
                          <span className={classes.meal_text}>
                            <strong>{sec.type}</strong>:{" "}
                            {items.map((i) => i.name).join(", ")}
                          </span>
                          <span className={classes.meal_option_cal}>
                            {items.reduce((s, i) => s + (i.cal ?? 0), 0)} kcal
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <div className={classes.macro_bar}>
                    <div className={classes.macro_item}>
                      <span className={classes.macro_label}>Calories</span>
                      <span className={classes.macro_value}>{dayTotal}</span>
                      <span className={classes.macro_unit}>kcal</span>
                    </div>
                    <div className={classes.macro_divider} />
                    <div className={classes.macro_item}>
                      <span className={classes.macro_label}>Protein</span>
                      <span className={classes.macro_value}>{dayProtein}</span>
                      <span className={classes.macro_unit}>g</span>
                    </div>
                    <div className={classes.macro_divider} />
                    <div className={classes.macro_item}>
                      <span className={classes.macro_label}>Fat</span>
                      <span className={classes.macro_value}>{dayFat}</span>
                      <span className={classes.macro_unit}>g</span>
                    </div>
                  </div>
                </>
              )}

              <button
                type="button"
                className={classes.view_plan_btn}
                onClick={() => navigate("/diet-plans")}
              >
                View Full Plan
              </button>
            </div>
          </div>
        </main>
      </div>

      {/* ── Reschedule modal ── */}
      {showReschedule && (
        <div
          className={classes.modal_overlay}
          onClick={() => setShowReschedule(false)}
        >
          <div
            className={classes.modal_box}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={classes.modal_close}
              onClick={() => setShowReschedule(false)}
              aria-label="Close"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <BookAppointment
              onClose={() => setShowReschedule(false)}
              isActive={profile?.status === "active"}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
